import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import {HttpClient, provideHttpClient} from '@angular/common/http';
import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {TranslateModule, TranslateLoader, TranslateService} from "@ngx-translate/core";
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeuix/themes/lara';
import { environment } from '../environments/environment';
import { applyVenueLanguage, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './_services/venue-language';
import { VenueLocale } from './_services/venue-locale';


// The i18n JSON files are copied verbatim out of public/, so unlike the JS and
// CSS bundles they never get a content hash and always live at the same URL.
// Without the version query a browser keeps serving its cached copy after a
// release, and any key added in that release renders as the raw key
// (e.g. "general.addadiscountcode") until the cache happens to expire.
const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) =>
    new TranslateHttpLoader(http, './i18n/', `.json?v=${environment.version}`);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }), 
    provideRouter(routes), 
    provideClientHydration(), 
    provideAnimationsAsync(),
    providePrimeNG({
            theme: {
                preset: Lara,
                options: {
                    darkModeSelector: 'none'
                }
            }
        }),
    provideHttpClient(),
    provideZoneChangeDetection(),
    importProvidersFrom([TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
    })]),

    // Pick the venue's language and load it, before the app renders.
    //
    // The language comes from the venue's Culture setting, so this looks the
    // venue up from the URL and reads its settings. Doing it here rather than in
    // a component is what keeps the first paint correct: the loader fetches
    // i18n/<lang>.json over HTTP, and until it lands the translation table is
    // empty. The `| translate` pipe copes - it re-renders when the file arrives -
    // but translate.instant() does not. It returns the key it was given, and a
    // component that builds a message during its constructor or ngOnInit
    // (payment's expired-link and missing-JWT messages, for instance) would show
    // "state.paymentlinkexpired" to the customer and never correct itself.
    //
    // Returning the observable makes Angular wait for it. applyVenueLanguage
    // never errors and times out on a slow venue lookup, so a failing API costs
    // the customer English text rather than a page that never boots.
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      translate.addLangs(SUPPORTED_LANGUAGES);
      translate.setDefaultLang(DEFAULT_LANGUAGE);
      return applyVenueLanguage(inject(HttpClient), translate, inject(VenueLocale));
    })
  ],
};
